import { motion } from "framer-motion";
import { Calendar, MapPin, Briefcase, ChevronRight } from "lucide-react";
import { Experience } from "@/lib/index";
interface ExperienceCardProps {
  experience: Experience;
}

/**
 * ExperienceCard component for displaying professional history.
 * Follows the 'Studio' feel with precise typography and high-end minimalism.
 */
export function ExperienceCard({
  experience
}: ExperienceCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-50px"
  }} transition={{
    type: "spring",
    stiffness: 300,
    damping: 30
  }} className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20">
      {/* Subtle Glow Effect on Hover */}
      <div className="absolute -inset-px bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative z-10">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          {/* Company Info & Role */}
          <div className="flex gap-5 items-start">
            <div className="relative shrink-0">
              {experience.logoUrl ? <div className="w-14 h-14 rounded-xl border border-border bg-white p-2 flex items-center justify-center overflow-hidden shadow-sm">
                  <img src={experience.logoUrl} alt={experience.company} className="w-full h-full object-contain" />
                </div> : <div className="w-14 h-14 rounded-xl border border-border bg-muted flex items-center justify-center shadow-sm">
                  <Briefcase className="w-7 h-7 text-muted-foreground" />
                </div>}
              {/* Role Indicator Dot */}
              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card bg-primary shadow-sm" />
            </div>

            <div className="space-y-1">
              <h3 className="text-xl md:text-2xl font-semibold font-sans tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
                {experience.role}
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-foreground/80">{experience.company}</span>
                {experience.location && <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <span className="w-1 h-1 rounded-full bg-border" />
                    <MapPin className="w-3 h-3" />
                    {experience.location}
                  </span>}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="flex items-center gap-2 shrink-0 md:mt-1.5">
            <div className="px-3 py-1 rounded-full bg-secondary/50 border border-border">
              <div className="flex items-center gap-2 text-xs font-mono font-medium text-muted-foreground uppercase tracking-wider">
                <Calendar className="w-3.5 h-3.5 text-primary" />
                <span>{experience.duration}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Key Achievements */}
        <div className="mt-8 space-y-4">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">
            <div className="h-[1px] w-6 bg-border" />
            <span>Achievements & Contributions</span>
          </div>
          
          <ul className="grid gap-3.5">
            {experience.achievements.map((achievement, index) => {})}
          </ul>
        </div>
      </div>

      {/* Aesthetic Bottom Border */}
      <div className="absolute bottom-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary/5 via-primary/20 to-primary/5 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
    </motion.div>;
}